import React, { useState, useEffect, useMemo } from 'react';
import { eventsAPI, eventTypesAPI, playertoolsAPI } from '../../services/api';

const EMPTY_FORM = {
  eid: '',
  tid: '',
  plid: '',
  playerOut: '',
  playerIn: '',
  minute: '',
};

function isSubstitutionEventType(eventType) {
  if (!eventType) return false;
  if (Number(eventType.edit) === 5) return true;
  const name = String(eventType.name || '').toLowerCase();
  return /wechsel|auswechsl|einwechsl|substitution|\bsub\b/.test(name);
}

function playerLabel(player) {
  if (!player) return '';
  return `${player.surname || ''} ${player.prename || ''}`.trim();
}

function formatEventPlayers(event) {
  if (event.type === 5 || isSubstitutionEventType(event.eid)) {
    const outName = playerLabel(event.playerOut);
    const inName = playerLabel(event.playerIn);
    if (outName && inName) return `${outName} ⬇️ / ${inName} ⬆️`;
    if (inName) return `${inName} ⬆️`;
    if (outName) return `${outName} ⬇️`;
  }
  return playerLabel(event.plid);
}

const GameEvents = ({ game, onClose }) => {
  const [events, setEvents] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [playerAssignments, setPlayerAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const selectedEventType = useMemo(
    () => eventTypes.find((type) => type._id === formData.eid),
    [eventTypes, formData.eid]
  );
  const isSubstitution = isSubstitutionEventType(selectedEventType);

  const teamPlayers = useMemo(() => {
    if (!formData.tid) return [];
    return playerAssignments.filter(
      (assignment) => assignment.tid?._id === formData.tid
        && assignment.pid?._id === game.pid._id
    );
  }, [formData.tid, playerAssignments, game.pid._id]);

  useEffect(() => {
    fetchEvents();
    fetchEventTypes();
    fetchPlayerAssignments();
  }, [game._id]);

  const fetchEvents = async () => {
    try {
      const response = await eventsAPI.getByGame(game._id);
      setEvents(response.data || []);
    } catch (err) {
      console.error('Failed to fetch events:', err);
      setError('Events konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const fetchEventTypes = async () => {
    try {
      const response = await eventTypesAPI.getAll();
      setEventTypes(response.data || []);
    } catch (err) {
      console.error('Failed to fetch event types:', err);
    }
  };

  const fetchPlayerAssignments = async () => {
    try {
      const response = await playertoolsAPI.getAll({ pid: game.pid._id });
      const assignmentsData = response.data?.playertools || response.data || [];
      setPlayerAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
    } catch (err) {
      console.error('Failed to fetch player assignments:', err);
      setPlayerAssignments([]);
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setShowAddForm(false);
  };

  const handleEventTypeChange = (eid) => {
    setFormData({
      ...EMPTY_FORM,
      eid,
      minute: formData.minute,
    });
  };

  const handleTeamChange = (tid) => {
    setFormData({
      ...formData,
      tid,
      plid: '',
      playerOut: '',
      playerIn: '',
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isSubstitution) {
      if (!formData.playerOut || !formData.playerIn) {
        setError('Bitte Spieler raus und Spieler rein auswählen.');
        return;
      }
      if (formData.playerOut === formData.playerIn) {
        setError('Ein- und auswechselnder Spieler müssen unterschiedlich sein.');
        return;
      }
    }

    try {
      const eventData = {
        pid: game.pid._id,
        rid: game.rid._id,
        gid: game._id,
        eid: formData.eid,
        tid: formData.tid,
        minute: parseInt(formData.minute, 10) || 0,
      };

      if (isSubstitution) {
        eventData.playerOut = formData.playerOut;
        eventData.playerIn = formData.playerIn;
      } else {
        eventData.plid = formData.plid || null;
      }

      await eventsAPI.create(eventData);
      resetForm();
      fetchEvents();
      setSuccessMessage('Event erfolgreich hinzugefügt!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error adding event:', err);
      setError(err.response?.data?.message || 'Event konnte nicht gespeichert werden');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Dieses Event wirklich löschen?')) return;

    try {
      await eventsAPI.delete(eventId);
      fetchEvents();
      setSuccessMessage('Event gelöscht!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      setError('Event konnte nicht gelöscht werden');
    }
  };

  const renderPlayerOptions = (excludeId = '') => teamPlayers
    .filter((assignment) => assignment.plid?._id !== excludeId)
    .map((assignment) => (
      <option key={assignment._id} value={assignment.plid?._id}>
        #{assignment.num} {assignment.plid?.surname} {assignment.plid?.prename}
      </option>
    ));

  if (loading) return <div className="loading loading-spinner loading-lg" />;

  return (
    <dialog open className="modal">
      <div className="modal-box w-11/12 max-w-4xl">
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-bold text-lg">
            Spiel-Events: {game.hid?.name} vs {game.aid?.name}
          </h3>
          <button type="button" className="btn btn-sm btn-circle btn-ghost" onClick={onClose}>
            ✕
          </button>
        </div>

        {error && (
          <div className="alert alert-error mb-4">
            <span>{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="alert alert-success mb-4">
            <span>{successMessage}</span>
          </div>
        )}

        <div className="mb-6">
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setShowAddForm(!showAddForm)}
          >
            {showAddForm ? 'Abbrechen' : 'Event hinzufügen'}
          </button>
        </div>

        {showAddForm && (
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
            <h4 className="font-semibold mb-4">Neues Event</h4>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Event-Typ *</span>
                  </label>
                  <select
                    required
                    value={formData.eid}
                    onChange={(e) => handleEventTypeChange(e.target.value)}
                    className="select select-bordered"
                  >
                    <option value="">Typ wählen</option>
                    {eventTypes.map((type) => (
                      <option key={type._id} value={type._id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Team {isSubstitution ? '*' : ''}</span>
                  </label>
                  <select
                    required={isSubstitution}
                    value={formData.tid}
                    onChange={(e) => handleTeamChange(e.target.value)}
                    className="select select-bordered"
                  >
                    <option value="">Team wählen</option>
                    <option value={game.hid._id}>{game.hid.name}</option>
                    <option value={game.aid._id}>{game.aid.name}</option>
                  </select>
                </div>

                {!isSubstitution ? (
                  <div className="form-control">
                    <label className="label">
                      <span className="label-text">Spieler</span>
                    </label>
                    <select
                      value={formData.plid}
                      onChange={(e) => setFormData({ ...formData, plid: e.target.value })}
                      className="select select-bordered"
                      disabled={!formData.tid}
                    >
                      <option value="">
                        {formData.tid ? 'Spieler wählen' : 'Zuerst Team wählen'}
                      </option>
                      {renderPlayerOptions()}
                    </select>
                  </div>
                ) : (
                  <>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Spieler raus *</span>
                      </label>
                      <select
                        required
                        value={formData.playerOut}
                        onChange={(e) => setFormData({ ...formData, playerOut: e.target.value })}
                        className="select select-bordered"
                        disabled={!formData.tid}
                      >
                        <option value="">
                          {formData.tid ? 'Auswechseln wählen' : 'Zuerst Team wählen'}
                        </option>
                        {renderPlayerOptions(formData.playerIn)}
                      </select>
                    </div>
                    <div className="form-control">
                      <label className="label">
                        <span className="label-text">Spieler rein *</span>
                      </label>
                      <select
                        required
                        value={formData.playerIn}
                        onChange={(e) => setFormData({ ...formData, playerIn: e.target.value })}
                        className="select select-bordered"
                        disabled={!formData.tid}
                      >
                        <option value="">
                          {formData.tid ? 'Einwechseln wählen' : 'Zuerst Team wählen'}
                        </option>
                        {renderPlayerOptions(formData.playerOut)}
                      </select>
                    </div>
                  </>
                )}

                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Minute</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="120"
                    value={formData.minute}
                    onChange={(e) => setFormData({ ...formData, minute: e.target.value })}
                    className="input input-bordered"
                    placeholder="0"
                  />
                </div>
              </div>

              {isSubstitution && formData.tid && (
                <p className="text-sm text-gray-600">
                  Wechsel für {teamPlayers.length} Kader-Spieler dieses Teams.
                </p>
              )}

              <div className="flex gap-2">
                <button type="submit" className="btn btn-primary">
                  Speichern
                </button>
                <button type="button" className="btn btn-ghost" onClick={resetForm}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="space-y-3">
          <h4 className="font-semibold">Events ({events.length})</h4>

          {events.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-4xl mb-2">⚽</div>
              <p className="text-gray-500">Noch keine Events für dieses Spiel.</p>
            </div>
          ) : (
            <div className="space-y-2">
              {events
                .sort((a, b) => a.minute - b.minute)
                .map((event) => (
                  <div
                    key={event._id}
                    className="bg-white border border-gray-200 rounded-lg p-3 flex items-center justify-between"
                  >
                    <div className="flex items-center gap-4 flex-wrap">
                      <div className="bg-blue-100 text-blue-800 text-sm font-medium px-2 py-1 rounded">
                        {event.minute}&apos;
                      </div>
                      <div className="font-medium">
                        {event.eid?.name || 'Unbekannt'}
                      </div>
                      {event.tid?.name && (
                        <div className="text-sm text-gray-600">{event.tid.name}</div>
                      )}
                      {formatEventPlayers(event) && (
                        <div className="text-sm text-gray-600">
                          {formatEventPlayers(event)}
                        </div>
                      )}
                    </div>

                    <button
                      type="button"
                      className="btn btn-sm btn-error btn-square"
                      onClick={() => handleDelete(event._id)}
                      title="Event löschen"
                    >
                      🗑️
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>

        <div className="modal-action">
          <button type="button" className="btn" onClick={onClose}>
            Schließen
          </button>
        </div>
      </div>
    </dialog>
  );
};

export default GameEvents;
